// Date formatting and manipulation utilities

import {
  format,
  addDays as dateFnsAddDays,
  differenceInDays,
  isWithinInterval,
  startOfWeek,
} from 'date-fns';
import { he } from 'date-fns/locale';

/**
 * Format a date to a localized string (Hebrew)
 * @param {Date|string} date - The date to format
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatStr = 'dd MMMM yyyy') => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, formatStr, { locale: he });
};

/**
 * Format a date to a short string (DD/MM/YYYY)
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatShortDate = date => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'dd/MM/yyyy', { locale: he });
};

/**
 * Add days to a date
 * @param {Date} date - The starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date
 */
export const addDays = (date, days) => {
  return dateFnsAddDays(date, days);
};

/**
 * Calculate the difference in days between two dates
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {number} Number of days difference
 */
export const daysBetween = (date1, date2) => {
  return differenceInDays(date2, date1);
};

/**
 * Check if a date is within a range
 * @param {Date} date - Date to check
 * @param {Date} startDate - Start of range
 * @param {Date} endDate - End of range
 * @returns {boolean} True if date is within range
 */
export const isDateInRange = (date, startDate, endDate) => {
  return isWithinInterval(date, { start: startDate, end: endDate });
};

/**
 * Get the start of a week for a given date
 * @param {Date} date - The date
 * @returns {Date} Start of the week
 */
export const getStartOfWeek = date => {
  return startOfWeek(date, { locale: he });
};

/**
 * Format a duration in days to a human-readable string
 * @param {number} days - Number of days
 * @returns {string} Formatted duration string in Hebrew
 */
export const formatDuration = days => {
  if (days === 0) return 'היום';
  if (days === 1) return 'יום אחד';
  return `${days} ימים`;
};
